import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Inbox, Phone, Calendar, Clock, Check, X, Loader2, RefreshCw, Stethoscope, MessageSquare,
} from 'lucide-react';
import DrawerShell from './DrawerShell';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { formatDateTimeIST } from '../utils/date';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const STATUS_TONES = {
  tentative: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: '#f59e0b' },
  confirmed: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: '#059669' },
  cancelled: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: '#dc2626' },
};

export default function BookingRequestsDrawer({ open, onClose, onActioned }) {
  const [tab, setTab] = useState('tentative');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [acting, setActing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ax.get('/api/clinic-ops/booking-requests', { params: { status: tab } });
      setRows(data?.requests || []);
    } catch (_) { toast.error('Could not load requests'); }
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  useEffect(() => {
    setNotes(selected?.notes || '');
  }, [selected?.id]);

  const confirm = async (id) => {
    if (!window.confirm('Confirm this booking? A real queue entry will be created.')) return;
    setActing(true);
    try {
      const { data } = await ax.post(`/api/clinic-ops/booking-requests/${id}/confirm`, {});
      toast.success(`Confirmed — queue #${data.queue_no}`);
      setSelected(null);
      reload();
      onActioned?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Confirm failed');
    }
    setActing(false);
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this booking request?')) return;
    setActing(true);
    try {
      await ax.patch(`/api/clinic-ops/booking-requests/${id}`, { status: 'cancelled', notes });
      toast.success('Cancelled');
      setSelected(null);
      reload();
      onActioned?.();
    } catch (_) { toast.error('Failed'); }
    setActing(false);
  };

  const saveNote = async () => {
    if (!selected) return;
    setActing(true);
    try {
      await ax.patch(`/api/clinic-ops/booking-requests/${selected.id}`, { notes });
      toast.success('Notes saved');
      reload();
    } catch (_) { toast.error('Failed'); }
    setActing(false);
  };

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      eyebrow="Public Bookings"
      title="Booking Requests"
      subtitle="Walk-up appointments submitted from the landing page"
      accent="#0891b2"
      icon={<Inbox className="h-5 w-5" />}
      width="xl"
      testId="booking-requests-drawer"
      headerExtras={
        <div className="flex gap-1 bg-white/15 ring-1 ring-white/30 rounded-lg p-0.5 inline-flex">
          {['tentative', 'confirmed', 'cancelled'].map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); setSelected(null); }}
              className={`px-3 py-1 rounded-md text-[11px] font-bold capitalize transition ${
                tab === t ? 'bg-white text-zinc-900' : 'text-white/80 hover:bg-white/10'
              }`}
              data-testid={`booking-tab-${t}`}
            >
              {t}
            </button>
          ))}
          <button onClick={reload} className="px-2 text-white/80 hover:text-white" title="Refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] h-full">
        {/* LIST */}
        <div className="border-r bg-white">
          {loading && rows.length === 0 ? (
            <div className="py-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-xs text-zinc-400">
              <Inbox className="h-7 w-7 mx-auto opacity-30 mb-1.5" />
              No {tab} requests.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100" data-testid="booking-list">
              {rows.map(r => (
                <li
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className={`p-3 cursor-pointer transition ${selected?.id === r.id ? 'bg-cyan-50 border-l-4 border-cyan-500' : 'hover:bg-zinc-50 border-l-4 border-transparent'}`}
                  data-testid={`booking-row-${r.id}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-cyan-100 to-blue-100 text-cyan-700 font-bold flex items-center justify-center flex-shrink-0">
                      {(r.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-zinc-900 truncate">{r.name}</div>
                      <div className="text-[11px] text-zinc-500 truncate"><Phone className="h-2.5 w-2.5 inline mr-0.5" />{r.phone}</div>
                      {r.preferred_date && (
                        <div className="text-[10px] text-zinc-400 mt-0.5">
                          <Calendar className="h-2.5 w-2.5 inline mr-0.5" />
                          {r.preferred_date} · {r.preferred_slot || '—'}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* DETAIL */}
        <div className="overflow-y-auto p-5">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-center text-zinc-400">
              <div>
                <Inbox className="h-10 w-10 mx-auto opacity-30 mb-2" />
                <p className="text-sm">Select a request to review and action.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Patient card */}
              <div className="rounded-xl border bg-white p-4">
                <div className="flex items-start gap-3">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-cyan-100 to-blue-100 text-cyan-700 font-extrabold text-xl flex items-center justify-center flex-shrink-0">
                    {(selected.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-extrabold text-zinc-900 truncate" style={{ fontFamily: 'Manrope' }}>{selected.name}</h3>
                    <div className="text-xs text-zinc-500 flex flex-wrap gap-3 mt-0.5">
                      <span><Phone className="h-3 w-3 inline mr-0.5" />{selected.phone}</span>
                      {selected.age && <span>{selected.age}y</span>}
                      {selected.gender && <span className="capitalize">{selected.gender}</span>}
                    </div>
                    <div className="mt-2">
                      {STATUS_TONES[selected.status] && (
                        <span className={`text-[10px] uppercase tracking-wider font-bold border px-2 py-0.5 rounded-full ${STATUS_TONES[selected.status].bg} ${STATUS_TONES[selected.status].text} ${STATUS_TONES[selected.status].border}`}>
                          {selected.status}
                        </span>
                      )}
                      <span className="text-[10px] text-zinc-400 ml-2">submitted {formatDateTimeIST(selected.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Visit request */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border bg-white p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5 flex items-center gap-1"><Calendar className="h-3 w-3" /> Preferred date</div>
                  <div className="text-sm font-bold text-zinc-900">{selected.preferred_date || '—'}</div>
                  <div className="text-xs text-zinc-500"><Clock className="h-3 w-3 inline mr-0.5" />{selected.preferred_slot || '—'}</div>
                </div>
                <div className="rounded-xl border bg-white p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5 flex items-center gap-1"><Stethoscope className="h-3 w-3" /> Preferred doctor</div>
                  <div className="text-sm font-bold text-zinc-900">{selected.preferred_doctor_name || 'Any'}</div>
                </div>
              </div>

              {/* Symptoms */}
              {selected.symptoms && (
                <div className="rounded-xl border bg-white p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> Patient describes</div>
                  <p className="text-sm text-zinc-800 leading-relaxed">{selected.symptoms}</p>
                </div>
              )}

              {/* Internal notes */}
              <div className="rounded-xl border bg-white p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Internal notes</div>
                  {selected.updated_at && (
                    <span className="ml-auto text-[10px] text-zinc-400">edited {formatDateTimeIST(selected.updated_at)}</span>
                  )}
                </div>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Confirmation call notes, callback time, etc."
                  className="text-sm min-h-[80px]"
                  data-testid="booking-notes-input"
                />
                <div className="flex justify-end mt-2">
                  <Button size="sm" variant="outline" onClick={saveNote} disabled={acting}>
                    Save note
                  </Button>
                </div>
              </div>

              {/* Actions */}
              {selected.status === 'tentative' && (
                <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-3 flex items-center gap-2">
                  <div className="flex-1 text-xs text-emerald-900">
                    Confirming will create a real queue entry linked to the patient.
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => cancel(selected.id)}
                    disabled={acting}
                    className="text-red-700 border-red-200 hover:bg-red-50"
                    data-testid="booking-cancel-btn"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />Cancel
                  </Button>
                  <Button
                    onClick={() => confirm(selected.id)}
                    disabled={acting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="booking-confirm-btn"
                  >
                    {acting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                    Confirm booking
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DrawerShell>
  );
}
